import { Button, CloseIcon, ProviderConsumer as FluentUIThemeConsumer, Loader, } from '@fluentui/react-northstar';
import React, { useEffect, useMemo } from 'react';
import { getFileIcon, getTruncatedFilename } from '../../EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent';
import { isImageFile } from '../../EditTicketAttachments/NewAttachmentFlow/fileIcons';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';

interface Props {
    onRemoveFile: (index: number) => void;
    file: File;
    id: number;
    isRemoveDisabled: boolean;
    isUploading: boolean;
    percentComplete: number;
    isInProgress: boolean;
}

/**
 * Every chip leads with the same fixed 32px square, so a row of pictures and documents stays even. A picture fills
 * it, cropped square; a file icon sits in the middle at its own size. Fixed and unshrinkable on purpose: the shared
 * thumbnail was only capped, so once the row of chips overflowed, flex squeezed every picture to 0px.
 */
const SLOT = { flex: '0 0 auto', width: '32px', height: '32px' };

const FileAttachments: React.FC<Props> = ({ isUploading, file, onRemoveFile, isRemoveDisabled, id, percentComplete, isInProgress }: Props) => {

    const removeFile = (index: number) => {
        onRemoveFile(index);
    }
    const extension = file.name.split(".").pop();
    const isPicture = isImageFile(extension, file);

    // One preview URL per file, released with the chip, rather than a new one on every render.
    const previewUrl = useMemo(() => (isPicture ? URL.createObjectURL(file) : null), [file, isPicture]);
    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    return (
        <FluentUIThemeConsumer
            render={globalTheme => (
                <>
                    <div className={`flex justify-between items-center my-1 py-2 pl-2 border border-tneutral-200 mr-2 mb-2 shadow-sm rounded bg-tneutral-100`} key={id}>
                        <span className='inline-flex items-center justify-center' style={SLOT}>
                            {isUploading
                                ? (isInProgress
                                    ? <div style={{ width: '1.5rem', height: '1.5rem' }}><CircularProgressbar value={percentComplete} strokeWidth={10} styles={buildStyles({ pathColor: globalTheme.siteVariables.colorScheme.brand.foreground, trailColor: "rgba(0,0,0,0)" })} /></div>
                                    : <Loader size='smaller' />)
                                : previewUrl
                                    ? <img src={previewUrl} alt='' style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '2px' }} />
                                    : getFileIcon(extension, { file }, globalTheme)}
                        </span>
                        <span className='px-2 whitespace-nowrap'>{getTruncatedFilename(file.name)}</span>
                        {!isUploading && <Button disabled={isRemoveDisabled} iconOnly text icon={<CloseIcon size='small' />} onClick={() => removeFile(id)} />}
                    </div>
                </>
            )} />
    )
}

export default FileAttachments;